import { useEffect, useState, RefObject } from 'react';

export type Size = { width: number; height: number };

// Track the container element's actual layout size. Uses ResizeObserver so
// the canvas re-measures when the container flexes for reasons other than a
// window resize — e.g. the sidebar collapses, a sibling toolbar mounts, or
// the route transitions in. Falls back to a window-resize listener for the
// rare browser without ResizeObserver.
export function useContainerSize(ref: RefObject<HTMLElement>, initial: Size = { width: 800, height: 600 }): Size {
  const [size, setSize] = useState<Size>(initial);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      setSize(prev => {
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        if (prev.width === w && prev.height === h) return prev;
        return { width: w, height: h };
      });
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      window.addEventListener('resize', measure);
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', measure);
      };
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref]);

  return size;
}
